import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';

interface FlatOption {
  id: any;
  label: string;
}

interface GroupedOption {
  groupLabel: string;
  items: FlatOption[];
}

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrls: ['./dropdown.component.scss']
})
export class DropdownComponent implements OnInit {
  @Input() placeholder = '';
  @Input() color: string;
  @Input() enableSearch = false;
  @Input() grouped = false;
  @Output() onChange = new EventEmitter<any>();

  @ViewChild('toggle', {static: false}) toggle: ElementRef;
  @ViewChild('menu', {static: false}) menu: ElementRef;
  @ViewChild('input', {static: false}) input: ElementRef;
  @ViewChild('dropdown', { static: false }) dropdown: NgbDropdown;

  private _options: FlatOption[] | GroupedOption[] = [];
  private _value: FlatOption;
  private _search: string;
  private _open = false;

  originalPlaceholder = '';
  styles: any;
  searchFocusEvent = new EventEmitter<any>();
  filteredFlat: FlatOption[] = [];
  filteredGroups: GroupedOption[] = [];

  constructor() {}

  ngOnInit() {
    switch (this.color) {
      case 'grey':
        this.styles = {
          'background-color': '#f5f8fa'
        };
        break;
    }
    this.originalPlaceholder = this.placeholder;
    this.filterOptions();
  }

  private filterOptions(): void {
    const term = (this.search || '').trim().toLowerCase();

    if (this.grouped) {
      // Grouped mode: iterate through the groups and filter each group.items
      const groups = this._options as GroupedOption[];
      if (!this.enableSearch || term === '') {
        this.filteredGroups = groups.map(g => ({
          groupLabel: g.groupLabel,
          items: [...g.items]
        }));
      } else {
        // Filter each group individually
        this.filteredGroups = groups
          .map(g => {
            const itensFiltrados = g.items.filter(opt =>
              opt.label.toLowerCase().includes(term)
            );
            return {
              groupLabel: g.groupLabel,
              items: itensFiltrados
            };
          })
          .filter(g => g.items.length > 0);
      }
      
      this.filteredFlat = [];
    } else {
      // Normal mode: Filter the array of simple options directly
      const flats = this._options as FlatOption[];
      if (!this.enableSearch || term === '') {
        this.filteredFlat = flats.slice();
      } else {
        this.filteredFlat = flats.filter(opt =>
          opt.label.toLowerCase().includes(term)
        );
      }

      this.filteredGroups = [];
    }
  }

  onEnter(event: KeyboardEvent) {
    event.preventDefault();
    event.stopPropagation();

    let optionToSelect: any;

    if (this.grouped) {
      // Grouped mode
      const g = this.filteredGroups.find(g => g.items.length > 0);
      if (g) {
        optionToSelect = g.items[0];
      }
    } else {
      // Flat mode
      optionToSelect = this.filteredFlat[0];
    }

    if (optionToSelect) {
      this.setValue(optionToSelect, this.dropdown);
    }
  }

  setValue(value: FlatOption, dropdown: NgbDropdown) {
    this.value = value;
    dropdown.close();
    this.open = false;
  }

  get options(): FlatOption[] | GroupedOption[] {
    return this._options;
  }

  @Input()
  set options(value: FlatOption[] | GroupedOption[]) {
    this._options = value;
    this.search = '';
    this.placeholder = this.originalPlaceholder || this.placeholder;
    this.filterOptions();
  }
  
  get value(): FlatOption {
    return this._value;
  }

  set value(value: FlatOption) {
    if (this._value !== value) {
      this._value = value;
      this.search = '';
      this.placeholder = value.label;
      this.onChange.emit(this._value);
    }
  }

  get search(): string {
    return this._search || '';
  }

  set search(value: string) {
    if (this._search !== value) {
      this._search = value;
      this.filterOptions();
    }
  }

  get open(): boolean {
    return this._open;
  }

  set open(value: boolean) {
    this.searchFocusEvent.emit(true);
    this._open = value;
  }
}
